import React from 'react';
import { Link, useParams } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowLeft, CheckCircle, Target, TrendingUp, Award } from 'lucide-react';
import Header from '../components/landing/Header';
import Footer from '../components/landing/Footer';
import { activities } from '../data/mock';

const ActivityDetailPage = () => {
  const { id } = useParams();
  const activity = activities.find(a => a.id === parseInt(id)) || activities[0];
  const IconComponent = LucideIcons[activity.icon] || LucideIcons.Circle;

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Back Link */}
          <Link to="/activities" className="inline-flex items-center gap-2 text-[#83928c] hover:text-[#1a1a2e] transition-colors mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Activities
          </Link>

          {/* Hero */}
          <div className="flex items-start gap-6 mb-12">
            <div 
              className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${activity.color}15` }}
            >
              <IconComponent 
                className="w-10 h-10" 
                style={{ color: activity.color }}
              />
            </div>
            <div>
              <h1 className="heading-2 mb-2">{activity.name}</h1>
              <p className="body-large">{activity.description}</p>
            </div>
          </div>

          {/* Skills */}
          <div className="bg-white rounded-2xl p-6 border border-black/10 mb-8">
            <h3 className="font-semibold text-lg text-[#1a1a2e] mb-4">Skills You'll Develop</h3>
            <div className="flex flex-wrap gap-3">
              {activity.skills.map((skill) => (
                <div key={skill} className="flex items-center gap-2 bg-[#3459a4]/10 px-4 py-2 rounded-full">
                  <CheckCircle className="w-4 h-4 text-[#3459a4]" />
                  <span className="text-sm font-medium text-[#1a1a2e]">{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* TPI Alignment */}
          <div className="bg-white rounded-2xl p-6 border border-black/10 mb-8">
            <h3 className="font-semibold text-lg text-[#1a1a2e] mb-4">TPI Score Contribution</h3>
            <p className="text-sm text-[#83928c] mb-4">
              This activity contributes to your overall TPI score across these dimensions:
            </p>
            <div className="space-y-3">
              {Object.entries(activity.tpiAlignment).map(([key, value]) => (
                <div key={key}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="capitalize text-[#1a1a2e]">{key}</span>
                    <span className="text-[#3459a4] font-medium">{value}%</span>
                  </div>
                  <div className="progress-bar">
                    <div 
                      className="progress-fill"
                      style={{ 
                        width: `${value}%`,
                        background: activity.color
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-white rounded-2xl p-6 border border-black/10 mb-8">
            <h3 className="font-semibold text-lg text-[#1a1a2e] mb-4">How This Activity Works</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="text-center p-4">
                <Target className="w-8 h-8 text-[#3459a4] mx-auto mb-2" />
                <h4 className="font-medium text-[#1a1a2e] mb-1">Practice</h4>
                <p className="text-xs text-[#83928c]">Complete exercises and get AI feedback</p>
              </div>
              <div className="text-center p-4">
                <TrendingUp className="w-8 h-8 text-[#22c55e] mx-auto mb-2" />
                <h4 className="font-medium text-[#1a1a2e] mb-1">Build Accuracy</h4>
                <p className="text-xs text-[#83928c]">Reach 80% accuracy score</p>
              </div>
              <div className="text-center p-4">
                <Award className="w-8 h-8 text-[#f97316] mx-auto mb-2" />
                <h4 className="font-medium text-[#1a1a2e] mb-1">Pass Assessment</h4>
                <p className="text-xs text-[#83928c]">Score 80%+ to complete module</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <a href="https://app.tenses.ai" className="btn-primary">
              Start Practicing Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ActivityDetailPage;
